import is from '@sindresorhus/is';
import { z } from 'zod';

// The components the agent may compose. Kept free of @json-render so the tool and the block checks
// stay in the main bundle while the renderer loads only once a card shows.

export const NAVIGATE_ACTION = 'navigate';
export const SEND_MESSAGE_ACTION = 'sendMessage';

export const MAX_TABLE_COLUMNS = 8;
export const MAX_TABLE_ROWS = 50;
export const MAX_TABS = 6;
export const MAX_TIMELINE_ITEMS = 20;
export const MAX_CHOICE_OPTIONS = 8;
export const MAX_LIST_ITEMS = 20;
export const MAX_LINE_POINTS = 24;
export const MAX_ENTITY_CARDS = 12;

const gapSchema = z.enum(['sm', 'md', 'lg']);
const toneSchema = z.enum(['gray', 'info', 'success', 'warning', 'error']);
const calloutToneSchema = z.enum(['info', 'success', 'warning', 'error']);
const timelineStatusSchema = z.enum(['pending', 'running', 'done', 'failed', 'skipped']);

export type TimelineStatus = z.infer<typeof timelineStatusSchema>;
export type Tone = z.infer<typeof toneSchema>;

export const navigateParamsSchema = z.strictObject({ path: z.string() });
export const sendMessageParamsSchema = z.strictObject({ text: z.string() });

export const generativeUiComponents = {
    Stack: {
        description: 'Vertical layout of its children.',
        hasChildren: true,
        props: z.strictObject({ gap: gapSchema }),
    },
    Row: {
        description: 'Horizontal layout of its children; wraps on narrow screens.',
        hasChildren: true,
        props: z.strictObject({ align: z.enum(['start', 'center', 'between']), gap: gapSchema }),
    },
    Grid: {
        description: 'Grid of equal columns, for metrics or cards side by side.',
        hasChildren: true,
        props: z.strictObject({ columns: z.union([z.literal(2), z.literal(3), z.literal(4)]) }),
    },
    Card: {
        description:
            'Bordered section with an optional title and subtitle (null when there is none) above its children. Use it as the root or for sections side by side; never put a Card directly inside another Card.',
        hasChildren: true,
        props: z.strictObject({ subtitle: z.string().nullable(), title: z.string().nullable() }),
    },
    Tabs: {
        description: `Tabbed views: one label per child, in order; 2 to ${MAX_TABS} tabs.`,
        hasChildren: true,
        props: z.strictObject({ labels: z.array(z.string()) }),
    },
    Collapsible: {
        description: 'Section the user can open and close; isOpen sets whether it starts open.',
        hasChildren: true,
        props: z.strictObject({ isOpen: z.boolean(), title: z.string() }),
    },
    Text: {
        description: 'A heading, a paragraph or a caption.',
        hasChildren: false,
        props: z.strictObject({ text: z.string(), variant: z.enum(['heading', 'body', 'caption']) }),
    },
    Metric: {
        description: 'One figure with its label and an optional hint (null when there is none).',
        hasChildren: false,
        props: z.strictObject({ hint: z.string().nullable(), label: z.string(), value: z.string() }),
    },
    Badge: {
        description: 'Short status or category label.',
        hasChildren: false,
        props: z.strictObject({ label: z.string(), tone: toneSchema }),
    },
    Progress: {
        description: 'Labelled progress bar showing value out of max.',
        hasChildren: false,
        props: z.strictObject({ label: z.string(), max: z.number(), value: z.number() }),
    },
    Table: {
        description: `Read-only table: column headers and rows of cell texts, each row as long as columns; up to ${MAX_TABLE_COLUMNS} columns and ${MAX_TABLE_ROWS} rows.`,
        hasChildren: false,
        props: z.strictObject({ columns: z.array(z.string()), rows: z.array(z.array(z.string())) }),
    },
    Timeline: {
        description: `Ordered steps: title, subtitle or null, status null (plain step) or pending, running, done, failed, skipped; up to ${MAX_TIMELINE_ITEMS}. Update steps as the plan changes; skipped only once a step will not run, with the reason as subtitle (waiting for an answer stays pending). End your reply with no step running.`,
        hasChildren: false,
        props: z.strictObject({
            items: z.array(
                z.strictObject({
                    status: timelineStatusSchema.nullable(),
                    subtitle: z.string().nullable(),
                    title: z.string(),
                }),
            ),
        }),
    },
    Callout: {
        description:
            'Highlighted note with a tone: a recommendation (info), a confirmation (success), a risk (warning) or a problem (error); optional title (null when there is none).',
        hasChildren: false,
        props: z.strictObject({ text: z.string(), title: z.string().nullable(), tone: calloutToneSchema }),
    },
    KeyValue: {
        description: `Compact list of facts, each a label and its value, e.g. the details of one item; up to ${MAX_LIST_ITEMS} items.`,
        hasChildren: false,
        props: z.strictObject({ items: z.array(z.strictObject({ label: z.string(), value: z.string() })) }),
    },
    BarChart: {
        description: `Horizontal bars comparing non-negative numbers, longest bar for the largest value, each with a label and an optional hint (null when there is none); unit is appended to every value (e.g. "%" or " roles", null for none); up to ${MAX_LIST_ITEMS} bars.`,
        hasChildren: false,
        props: z.strictObject({
            items: z.array(z.strictObject({ hint: z.string().nullable(), label: z.string(), value: z.number() })),
            unit: z.string().nullable(),
        }),
    },
    EmptyState: {
        description: 'Short message that there is nothing to show.',
        hasChildren: false,
        props: z.strictObject({ text: z.string() }),
    },
    Choice: {
        description: `A question with 2 to ${MAX_CHOICE_OPTIONS} options (label, description or null): radio buttons, or checkboxes when multiple is true. recommended is the label you suggest (null for none), marked and preselected. The picks reach you as the user's next message "question: label, label" in option order.`,
        hasChildren: false,
        props: z.strictObject({
            multiple: z.boolean(),
            options: z.array(z.strictObject({ description: z.string().nullable(), label: z.string() })),
            question: z.string(),
            recommended: z.string().nullable(),
            submitLabel: z.string(),
        }),
    },
    ChangeReceipt: {
        description:
            'The result of a change you made, right after it ran: verb sets the icon, caption names it in the conversation language (e.g. "Created role"), title is the item, detail one optional line (null for none). Its children follow below it, usually the item as a live card. Offer no undo.',
        hasChildren: true,
        props: z.strictObject({
            caption: z.string(),
            detail: z.string().nullable(),
            title: z.string(),
            verb: z.enum(['created', 'updated', 'deleted', 'published', 'archived']),
        }),
    },
    LineChart: {
        description: `A trend over ordered points (e.g. weeks), each a label and a number; title names the measure and the latest value is shown large; unit is appended to values (null for none); 2 to ${MAX_LINE_POINTS} points.`,
        hasChildren: false,
        props: z.strictObject({
            points: z.array(z.strictObject({ label: z.string(), value: z.number() })),
            title: z.string(),
            unit: z.string().nullable(),
        }),
    },
    EntityCard: {
        description: `Live card of an app item or list with current data; opens the item on click. Fill all ids from your tools, never invent them. At most ${MAX_ENTITY_CARDS} per UI. Its children are EntityCards of its own items and the only ones shown under it; with children: [] it shows all its items, or only itself when nested.`,
        hasChildren: true,
        props: z.strictObject({ entity: z.string(), ids: z.record(z.string(), z.string()) }),
    },
    Button: {
        description: `Opens an in-app page (${NAVIGATE_ACTION}) or sends the user's follow-up to you (${SEND_MESSAGE_ACTION}).`,
        hasChildren: false,
        props: z.strictObject({ label: z.string(), variant: z.enum(['primary', 'secondary', 'tertiary']) }),
    },
} as const;

export type GenerativeUiComponentType = keyof typeof generativeUiComponents;

export const NAVIGATE_DESCRIPTION = 'Open an in-app page. path must start with a single "/".';
export const SEND_MESSAGE_DESCRIPTION = 'Send text to the agent as if the user wrote it.';

const PATH_CHARACTERS = /^\/[\w\-.~!$&'()*+,;=:@/?#%]*$/;
const LAST_CONTROL_CHARACTER = 0x1f;
const DELETE = 0x7f;

const decode = (path: string): string | null => {
    try {
        return decodeURIComponent(path);
    } catch {
        return null;
    }
};

const hasUnsafeCharacter = (text: string): boolean =>
    [...text].some((character) => {
        const code = character.codePointAt(0) ?? 0;
        return code <= LAST_CONTROL_CHARACTER || code === DELETE || character === '\\' || /\s/u.test(character);
    });

/**
 * A path the agent may open: printable ASCII under one leading slash that, decoded, holds no "//",
 * backslash, whitespace or control character a browser could read as another origin.
 */
export const isInAppPath = (path: string): boolean => {
    if (!PATH_CHARACTERS.test(path) || path.includes('//')) return false;
    const decoded = decode(path);
    return !is.null(decoded) && !decoded.includes('//') && !hasUnsafeCharacter(decoded);
};
